import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDropzone } from "react-dropzone";
import { Upload, FileText, Sparkles, Check, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getCategoryConfig, getTypeConfig } from "@/constants/ui-mapping";
import confetti from "canvas-confetti";

interface ProcessingDocument {
  id: string;
  file: File;
  status: "uploading" | "analyzing" | "complete" | "error";
  progress: number;
  title: string;
  displayTitle: string;
  docType?: string;
  category?: string;
  error?: string;
}

interface OnboardingUploadExperienceProps {
  userId: string;
  organizationId?: string;
  onComplete?: (count: number) => void;
}

export const OnboardingUploadExperience = ({
  userId,
  organizationId,
  onComplete,
}: OnboardingUploadExperienceProps) => {
  const [documents, setDocuments] = useState<ProcessingDocument[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  const [typeCounts, setTypeCounts] = useState<Record<string, number>>({});

  // Typing animation effect
  const TypewriterText = ({ text, onComplete }: { text: string; onComplete?: () => void }) => {
    const [displayText, setDisplayText] = useState("");
    const [isComplete, setIsComplete] = useState(false);

    useEffect(() => {
      if (!text) return;
      
      let index = 0;
      const interval = setInterval(() => {
        if (index <= text.length) {
          setDisplayText(text.slice(0, index));
          index++;
        } else {
          clearInterval(interval);
          setIsComplete(true);
          onComplete?.();
        }
      }, 30); // Speed of typing

      return () => clearInterval(interval);
    }, [text, onComplete]);

    return (
      <span className={isComplete ? "" : "border-r-2 border-primary animate-pulse"}>
        {displayText}
      </span>
    );
  };

  const processDocument = useCallback(async (doc: ProcessingDocument, file: File) => {
    let newDocId: string | null = null;
    try {
      // Phase 1: Upload
      setDocuments((prev) =>
        prev.map((d) => (d.id === doc.id ? { ...d, status: "uploading", progress: 10 } : d))
      );

      const fileExt = file.name.split(".").pop()?.toLowerCase() || "pdf";
      const filePath = `${userId}/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage.from("documents").upload(filePath, file);
      if (uploadError) throw uploadError;

      setDocuments((prev) => prev.map((d) => (d.id === doc.id ? { ...d, progress: 30 } : d)));

      // Create document record
      const { data: newDoc, error: insertError } = await supabase
        .from("documents")
        .insert({
          user_id: userId,
          organization_id: organizationId,
          title: file.name,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          mime_type: file.type || "application/pdf",
          source: "upload",
          status: "processing",
        })
        .select("id")
        .single();

      if (insertError) throw insertError;
      newDocId = newDoc.id;

      setDocuments((prev) => prev.map((d) => (d.id === doc.id ? { ...d, progress: 50, status: "analyzing" } : d)));

      // Phase 2: AI Analysis
      const { data: extractedInfo, error: extractError } = await supabase.functions.invoke("extract-document-info", {
        body: {
          fileName: file.name,
          mimeType: file.type || "application/pdf",
          filePath,
        },
      });

      if (extractError) throw extractError;
      if (extractedInfo?.error) throw new Error(extractedInfo.error);

      setDocuments((prev) => prev.map((d) => (d.id === doc.id ? { ...d, progress: 80 } : d)));

      // Update document with extracted info
      // IMPORTANT: Edge function returns "suggested_title", not "title"
      const updateData: Record<string, unknown> = {
        title: extractedInfo.suggested_title || extractedInfo.title || file.name,
        ai_summary: extractedInfo.summary || extractedInfo.ai_summary,
        doc_type: extractedInfo.doc_type || "other",
        extracted_text: extractedInfo.extracted_text,
        text_extracted: extractedInfo.text_extracted ?? !!extractedInfo.extracted_text,
        ocr_used: extractedInfo.used_ocr || extractedInfo.ocr_used || false,
        doc_date: extractedInfo.doc_date,
        expires_at: extractedInfo.expires_at,
        keywords: extractedInfo.keywords,
        confidence_score: extractedInfo.confidence_score,
        is_sensitive: extractedInfo.is_sensitive,
        key_fields: extractedInfo.key_fields,
        status: "complete",
      };

      if (extractedInfo.category) updateData.category = extractedInfo.category;

      await supabase.from("documents").update(updateData).eq("id", newDocId);

      // Phase 3: Complete with animation
      const finalTitle = extractedInfo.suggested_title || extractedInfo.title || file.name;
      setDocuments((prev) =>
        prev.map((d) =>
          d.id === doc.id
            ? {
                ...d,
                progress: 100,
                status: "complete",
                displayTitle: finalTitle,
                docType: extractedInfo.doc_type || "other",
                category: extractedInfo.category,
              }
            : d
        )
      );

      // Update type counts
      const docType = extractedInfo.doc_type || "other";
      setTypeCounts((prev) => ({ ...prev, [docType]: (prev[docType] || 0) + 1 }));
      setCompletedCount((prev) => prev + 1);

      return true;
    } catch (error: any) {
      const message = error?.message || "Erreur lors de l'analyse";
      console.error("Document processing error:", error);

      // IMPORTANT: avoid leaving the document stuck in 'processing' in the DB
      if (newDocId) {
        await supabase
          .from("documents")
          .update({
            status: "failed",
            description: `Erreur analyse: ${message}`,
          })
          .eq("id", newDocId);
      }

      setDocuments((prev) => prev.map((d) => (d.id === doc.id ? { ...d, status: "error", error: message } : d)));
      return false;
    }
  }, [userId, organizationId]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      setIsProcessing(true);

      // Initialize all documents
      const newDocs: ProcessingDocument[] = acceptedFiles.map((file) => ({
        id: crypto.randomUUID(),
        file,
        status: "uploading",
        progress: 0,
        title: file.name,
        displayTitle: "",
      }));

      setDocuments((prev) => [...prev, ...newDocs]);

      // Process in parallel batches of 3
      let successCount = 0;
      const batchSize = 3;
      for (let i = 0; i < newDocs.length; i += batchSize) {
        const batch = newDocs.slice(i, i + batchSize);
        const results = await Promise.all(batch.map((d) => processDocument(d, d.file)));
        successCount += results.filter(Boolean).length;
      }

      setIsProcessing(false);

      if (successCount > 0) {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ["#6366f1", "#8b5cf6", "#a855f7", "#22c55e"],
        });
        onComplete?.(successCount);
      }

      if (successCount === 0) {
        toast.error("Aucun document n'a pu être analysé. Vérifiez la taille et le nombre de pages.");
      }
    },
    [onComplete, processDocument]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".jpg", ".jpeg", ".png", ".heic", ".webp"],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    disabled: isProcessing,
  });

  const removeDocument = (id: string) => {
    setDocuments(prev => prev.filter(d => d.id !== id));
  };

  const allComplete = documents.length > 0 && documents.every(d => d.status === "complete" || d.status === "error");
  const globalProgress = documents.length > 0 
    ? documents.reduce((sum, d) => sum + d.progress, 0) / documents.length 
    : 0;

  return (
    <div className="space-y-4">
      {/* Dropzone - Compact */}
      <div
        {...getRootProps()}
        className={`
          relative overflow-hidden rounded-xl border-2 border-dashed p-4 text-center
          transition-all duration-300 cursor-pointer
          ${isDragActive 
            ? "border-primary bg-primary/10 scale-[1.01]" 
            : "border-border hover:border-primary/50 hover:bg-muted/50"
          }
          ${isProcessing ? "pointer-events-none opacity-60" : ""}
        `}
      >
        <input {...getInputProps()} />
        
        <motion.div
          initial={false}
          animate={{ scale: isDragActive ? 1.05 : 1 }}
          className="flex items-center justify-center gap-4"
        >
          <div className={`
            w-10 h-10 rounded-full flex items-center justify-center shrink-0
            ${isDragActive ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"}
          `}>
            <Upload className="w-5 h-5" />
          </div>
          
          <div className="text-left">
            <p className="text-sm font-semibold">
              {isDragActive ? "Déposez vos documents" : "Glissez vos documents ici"}
            </p>
            <p className="text-xs text-muted-foreground">
              ou cliquez pour sélectionner • PDF, Images • Max 10 Mo
            </p>
          </div>
        </motion.div>
      </div>

      {/* Global Progress */}
      {documents.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary animate-pulse" />
              <span className="font-medium">
                {isProcessing ? "Analyse en cours..." : "Analyse terminée"}
              </span>
            </div>
            <span className="text-muted-foreground">
              {completedCount} / {documents.length} documents
            </span>
          </div>
          
          <Progress value={globalProgress} className="h-2" />

          {/* Live Type Counter */}
          {Object.keys(typeCounts).length > 0 && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-wrap gap-2 justify-center"
            >
              {Object.entries(typeCounts).map(([type, count]) => {
                const config = getTypeConfig(type);
                return (
                  <motion.div
                    key={type}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 500, damping: 25 }}
                  >
                    <Badge variant="secondary" className="gap-1">
                      <span>{config.emoji}</span>
                      <span>{count}</span>
                      <span className="text-muted-foreground">{config.label}</span>
                    </Badge>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </motion.div>
      )}

      {/* Document Cards */}
      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        <AnimatePresence mode="popLayout">
          {documents.map((doc, index) => (
            <motion.div
              key={doc.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ delay: index * 0.05 }}
              className="relative"
            >
              <div className={`
                relative rounded-xl border p-4 overflow-hidden
                ${doc.status === "complete" ? "border-success/30 bg-success/5" : "border-border bg-card"}
                ${doc.status === "error" ? "border-destructive/30 bg-destructive/5" : ""}
              `}>
                {/* Scan Animation */}
                {(doc.status === "uploading" || doc.status === "analyzing") && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-b from-primary/20 via-primary/10 to-transparent"
                    initial={{ y: "-100%" }}
                    animate={{ y: "100%" }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  />
                )}

                <div className="relative flex items-center gap-4">
                  {/* Icon */}
                  <div className={`
                    w-12 h-12 rounded-lg flex items-center justify-center shrink-0
                    ${doc.status === "complete" ? "bg-success/20" : "bg-primary/10"}
                    ${doc.status === "error" ? "bg-destructive/20" : ""}
                  `}>
                    {doc.status === "complete" ? (
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 500, damping: 25 }}
                      >
                        <Check className="w-6 h-6 text-success" />
                      </motion.div>
                    ) : doc.status === "error" ? (
                      <X className="w-6 h-6 text-destructive" />
                    ) : (
                      <Loader2 className="w-6 h-6 text-primary animate-spin" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {/* Title with typewriter effect */}
                    <div className="font-medium truncate">
                      {doc.status === "complete" && doc.displayTitle ? (
                        <TypewriterText text={doc.displayTitle} />
                      ) : (
                        <span className="text-muted-foreground">{doc.title}</span>
                      )}
                    </div>

                    {/* Status / Badges */}
                    <div className="flex items-center gap-2 mt-1">
                      {doc.status === "uploading" && (
                        <span className="text-xs text-muted-foreground">
                          Téléchargement...
                        </span>
                      )}
                      {doc.status === "analyzing" && (
                        <span className="text-xs text-primary flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          Analyse IA en cours...
                        </span>
                      )}
                      {doc.status === "complete" && doc.docType && (
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ 
                            type: "spring", 
                            stiffness: 500, 
                            damping: 20,
                            delay: 0.5 
                          }}
                        >
                          <Badge variant="secondary" className="text-xs">
                            {getTypeConfig(doc.docType).emoji} {getTypeConfig(doc.docType).label}
                          </Badge>
                        </motion.div>
                      )}
                      {doc.status === "complete" && doc.category && (
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ 
                            type: "spring", 
                            stiffness: 500, 
                            damping: 20,
                            delay: 0.7 
                          }}
                        >
                          <Badge variant="outline" className="text-xs">
                            {getCategoryConfig(doc.category).emoji} {getCategoryConfig(doc.category).label}
                          </Badge>
                        </motion.div>
                      )}
                      {doc.status === "error" && (
                        <span className="text-xs text-destructive">
                          Erreur: {doc.error}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Remove button for completed/error */}
                  {(doc.status === "complete" || doc.status === "error") && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0 h-8 w-8"
                      onClick={() => removeDocument(doc.id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Progress bar */}
                {(doc.status === "uploading" || doc.status === "analyzing") && (
                  <div className="mt-3">
                    <Progress value={doc.progress} className="h-1" />
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Success message */}
      {allComplete && documents.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center p-6 rounded-xl bg-success/10 border border-success/30"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 500, damping: 25 }}
            className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-4"
          >
            <Check className="w-8 h-8 text-success" />
          </motion.div>
          <p className="text-lg font-semibold text-success">
            {completedCount} document{completedCount > 1 ? "s" : ""} analysé{completedCount > 1 ? "s" : ""} avec succès !
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Vos documents sont prêts et classés automatiquement
          </p>
        </motion.div>
      )}
    </div>
  );
};
